import { useEffect, useId, useMemo, useRef, useState } from "react"
import { CustomField, LexicalText } from "@/schema"
import type { SearchResult } from "@/schema/search"
import { filterUsersForUserReferenceField } from "@/utils/custom-field-users"
import { DateInput, MultiSelectInput, NumberInput, SelectInput, TextInput } from "@ui/forms"
import { Button, Checkbox, Label } from "@ui/shad"

import { useFetchCustomFields } from "@/hooks/api/custom_fields/queries"
import { useFetchUsers } from "@/hooks/api/users/queries"
import { SelectOptionDot } from "@/components/custom-fields/select-option"
import { SimpleFormDialog } from "@/components/dialogs"
import { SearchDialog } from "@/components/dialogs/search-dialog"
import { Editor } from "@/components/lexical"

interface BulkCustomFieldUpdateProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (fieldKey: string, value: string | string[] | number | boolean | null) => void
  isSubmitting: boolean
  selectedCount: number
  entityLabel: string
}

const DEFAULT_BACKEND_TARGET_TYPES = ["Household", "Business", "User"] as const
type BackendTargetType = (typeof DEFAULT_BACKEND_TARGET_TYPES)[number]

const BACKEND_TO_SEARCH_TYPE_MAP: Record<BackendTargetType, SearchResult["type"][]> = {
  Household: ["household", "client", "prospect"],
  Business: ["business"],
  User: ["user"],
}

const SEARCH_TYPE_TO_BACKEND_TYPE: Partial<Record<SearchResult["type"], BackendTargetType>> = {
  household: "Household",
  client: "Household",
  prospect: "Household",
  business: "Business",
  user: "User",
}

export function BulkCustomFieldUpdate({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  selectedCount,
  entityLabel,
}: BulkCustomFieldUpdateProps) {
  const dialogFormId = useId()

  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null)
  const [fieldValue, setFieldValue] = useState<string | string[] | number | boolean | null>(null)
  const [clearValue, setClearValue] = useState(false)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [crossRefLabel, setCrossRefLabel] = useState<string | null>(null)
  const richTextRef = useRef<string | null>(null)

  const { data: customFieldsResponse } = useFetchCustomFields("Household", { enabled: open })
  const { data: usersResponse } = useFetchUsers()

  const customFields = useMemo(() => customFieldsResponse?.custom_fields ?? [], [customFieldsResponse?.custom_fields])

  const selectedField = useMemo(
    () => customFields.find((f) => f.field_key === selectedFieldKey) ?? null,
    [customFields, selectedFieldKey]
  )

  useEffect(() => {
    if (!open) {
      setSelectedFieldKey(null)
      setFieldValue(null)
      setClearValue(false)
      setCrossRefLabel(null)
      richTextRef.current = null
    }
  }, [open])

  useEffect(() => {
    const field = customFields.find((f) => f.field_key === selectedFieldKey)
    setFieldValue(field?.field_type === "boolean" ? false : null)
    setClearValue(false)
    setCrossRefLabel(null)
    richTextRef.current = null
  }, [selectedFieldKey, customFields])

  const fieldOptions = useMemo(
    () =>
      customFields.map((f) => ({
        value: f.field_key,
        label: f.display_name,
      })),
    [customFields]
  )

  const userOptions = useMemo(() => {
    const filteredUsers = selectedField
      ? filterUsersForUserReferenceField(selectedField, usersResponse?.users)
      : (usersResponse?.users ?? [])

    return filteredUsers.map((user) => ({
      value: user.id,
      label: user.full_name,
    }))
  }, [selectedField, usersResponse?.users])

  function handleSubmit() {
    if (!selectedFieldKey) return

    if (clearValue) {
      onConfirm(selectedFieldKey, null)
      return
    }

    const submitValue = selectedField?.field_type === "rich_text" ? richTextRef.current : fieldValue
    onConfirm(selectedFieldKey, submitValue)
  }

  const crossRefSearchTypes = useMemo(() => {
    if (selectedField?.field_type !== "cross_reference") return undefined

    const rawConfigured =
      selectedField.cross_reference_allowed_target_types ?? selectedField.options?.cross_reference_allowed_target_types

    const configuredArray = Array.isArray(rawConfigured)
      ? rawConfigured
      : rawConfigured
        ? [rawConfigured]
        : Array.from(DEFAULT_BACKEND_TARGET_TYPES)

    const normalized = configuredArray
      .map((type) => DEFAULT_BACKEND_TARGET_TYPES.find((allowed) => allowed.toLowerCase() === type.toLowerCase()))
      .filter((type): type is BackendTargetType => Boolean(type))

    const backendTypes = normalized.length > 0 ? normalized : Array.from(DEFAULT_BACKEND_TARGET_TYPES)
    const searchTypes = backendTypes.flatMap((bt) => BACKEND_TO_SEARCH_TYPE_MAP[bt] ?? [])
    return searchTypes.length > 0 ? Array.from(new Set(searchTypes)) : undefined
  }, [selectedField])

  function handleCrossRefSelect(item: SearchResult) {
    const backendType = SEARCH_TYPE_TO_BACKEND_TYPE[item.type] ?? "Household"
    setFieldValue(JSON.stringify({ type: backendType, id: item.id }))
    setCrossRefLabel(item.display_label)
    setSearchDialogOpen(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return
    onOpenChange(nextOpen)
  }

  const hasValue =
    selectedField?.field_type === "boolean"
      ? fieldValue !== null
      : selectedField?.field_type === "rich_text"
        ? richTextRef.current !== null
        : fieldValue !== null && fieldValue !== ""

  const isConfirmDisabled = !selectedFieldKey || (!clearValue && !hasValue) || isSubmitting

  return (
    <>
      <SimpleFormDialog
        title={
          <>
            Update custom field for {selectedCount} {entityLabel}
            {selectedCount === 1 ? "" : "s"}
          </>
        }
        open={open}
        onOpenChange={handleOpenChange}
        action={{ label: "Update", form: dialogFormId, disabled: isConfirmDisabled, pending: isSubmitting }}
        cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: isSubmitting }}
      >
        <form
          id={dialogFormId}
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (isConfirmDisabled || isSubmitting) return
            void handleSubmit()
          }}
        >
          <div className="space-y-3">
            <SelectInput
              id="bulk-custom-field-select"
              label="Custom field"
              value={selectedFieldKey}
              onChange={(value: string | null) => setSelectedFieldKey(value)}
              options={fieldOptions}
              placeholder="Select a custom field"
            />

            {selectedField && (
              <>
                {clearValue ? (
                  <p className="text-muted-foreground text-sm">
                    The value for this field will be removed from the selected {entityLabel}
                    {selectedCount === 1 ? "" : "s"}.
                  </p>
                ) : (
                  <CustomFieldValueInput
                    field={selectedField}
                    value={fieldValue}
                    onChange={setFieldValue}
                    userOptions={userOptions}
                    onOpenCrossRefSearch={() => setSearchDialogOpen(true)}
                    crossRefLabel={crossRefLabel}
                    richTextRef={richTextRef}
                  />
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="bulk-custom-field-clear"
                    checked={clearValue}
                    onCheckedChange={(checked) => {
                      setClearValue(Boolean(checked))
                      // The rich_text Editor remounts empty when restored, so drop any
                      // stale HTML held in the ref to keep hasValue in sync with the UI.
                      richTextRef.current = null
                    }}
                  />
                  <Label htmlFor="bulk-custom-field-clear" className="text-sm font-normal">
                    Clear value
                  </Label>
                </div>
              </>
            )}
          </div>
        </form>
      </SimpleFormDialog>

      {selectedField?.field_type === "cross_reference" && (
        <SearchDialog
          open={searchDialogOpen}
          onOpenChange={setSearchDialogOpen}
          onSelect={handleCrossRefSelect}
          title="Select reference"
          placeholder="Search..."
          allowedTypes={crossRefSearchTypes}
          showPreview
          widthVariant="compact"
        />
      )}
    </>
  )
}

interface CustomFieldValueInputProps {
  field: CustomField
  value: string | string[] | number | boolean | null
  onChange: (value: string | string[] | number | boolean | null) => void
  userOptions: { value: string; label: string }[]
  onOpenCrossRefSearch: () => void
  crossRefLabel: string | null
  richTextRef: React.MutableRefObject<string | null>
}

function CustomFieldValueInput({
  field,
  value,
  onChange,
  userOptions,
  onOpenCrossRefSearch,
  crossRefLabel,
  richTextRef,
}: CustomFieldValueInputProps) {
  switch (field.field_type) {
    case "text":
      return (
        <TextInput
          id="bulk-custom-field-value"
          label="Value"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={`Enter ${field.display_name.toLowerCase()}...`}
        />
      )

    case "number":
      return (
        <NumberInput
          id="bulk-custom-field-value"
          label="Value"
          value={value as number | null}
          onChange={(val) => onChange(val)}
          placeholder={`Enter ${field.display_name.toLowerCase()}...`}
        />
      )

    case "date": {
      const parseLocalDate = (dateStr: string): Date | undefined => {
        if (!dateStr) return undefined
        const [year, month, day] = dateStr.split("-").map(Number)
        if (!year || !month || !day) return undefined
        return new Date(year, month - 1, day)
      }

      const formatDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }

      return (
        <DateInput
          id="bulk-custom-field-value"
          label="Value"
          value={value ? parseLocalDate(String(value)) : undefined}
          onChange={(date) => onChange(date ? formatDate(date) : null)}
          placeholder={`Select ${field.display_name.toLowerCase()}...`}
        />
      )
    }

    case "boolean":
      return (
        <div className="space-y-0.5">
          <Label>Value</Label>
          <div className="flex items-center gap-2 py-1">
            <Checkbox checked={(value as boolean) ?? false} onCheckedChange={(checked) => onChange(Boolean(checked))} />
            <span className="text-sm">{field.display_name}</span>
          </div>
        </div>
      )

    case "url":
      return (
        <TextInput
          id="bulk-custom-field-value"
          label="Value"
          type="url"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={`Enter ${field.display_name.toLowerCase()}...`}
        />
      )

    case "single_select": {
      const options =
        field.options?.select_options?.map((option) => ({
          label: (
            <span className="flex items-center gap-2">
              <SelectOptionDot customField={field} option={option} />
              {option}
            </span>
          ),
          textLabel: option,
          value: option,
        })) ?? []
      return (
        <SelectInput
          id="bulk-custom-field-value"
          label="Value"
          value={(value as string) ?? undefined}
          onChange={(val: string | null) => onChange(val ?? null)}
          options={options}
          placeholder={`Select ${field.display_name.toLowerCase()}...`}
        />
      )
    }

    case "multi_select": {
      const options = field.options?.select_options?.map((option) => ({ label: option, value: option })) ?? []
      return (
        <MultiSelectInput
          id="bulk-custom-field-value"
          label="Value"
          values={Array.isArray(value) ? value : []}
          onChange={onChange}
          options={options}
          placeholder={`Select ${field.display_name.toLowerCase()}...`}
        />
      )
    }

    case "user_reference":
      return (
        <SelectInput
          id="bulk-custom-field-value"
          label="Value"
          value={(value as string) ?? undefined}
          onChange={(val: string | null) => onChange(val ?? null)}
          options={userOptions}
          placeholder="Select a user..."
        />
      )

    case "cross_reference":
      return (
        <div className="space-y-0.5">
          <Label>Value</Label>
          <div className="flex items-center gap-2">
            <Button variant="outline" type="button" className="w-full justify-start" onClick={onOpenCrossRefSearch}>
              {crossRefLabel ? (
                <span className="truncate">{crossRefLabel}</span>
              ) : (
                <span className="text-muted-foreground">Select a reference...</span>
              )}
            </Button>
          </div>
        </div>
      )

    case "rich_text":
      return (
        <div className="space-y-0.5">
          <Label>Value</Label>
          <div className="border-input focus-within:border-ring rounded-md border">
            <Editor
              onChange={(lexicalText: LexicalText) => {
                richTextRef.current = lexicalText.html ?? null
              }}
              startingPlaceholder={`Enter ${field.display_name.toLowerCase()}...`}
              disableHeadings
              disableSlashCommands
              styleOverrides={{
                "--lexical-editor-font-size": "13px",
                "--lexical-editor-max-height": "12rem",
              }}
            />
          </div>
        </div>
      )

    default:
      return null
  }
}
